import { toast } from "@/components/ui";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { captureError } from "@/lib/monitoring";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useCallObject } from "@daily-co/daily-react";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useCallback } from "react";
import {
  callPanelOpenAtom,
  isDialingTranslatorAtom,
  translatorIdAtom,
  videoCallRoomTokenAtom,
} from "../atoms/videoCallAtoms";
import { useCompleteVideoVisitMeeting } from "../network/useCompleteVideoVisitMeeting";
import { useCreateVideoVisitMeeting } from "../network/useCreateVideoVIsitMeeting";
import {
  TRANSLATOR_AUDIO_ONLY_DISPLAY_NAME,
  TRANSLATOR_DISPLAY_NAME,
  TRANSLATOR_USER_ID,
  type TranslatorOption,
} from "../utils/translatorOptions";

export const useJoinCall = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const callObject = useCallObject({});
  const [translatorId, setTranslatorId] = useAtom(translatorIdAtom);
  const [isDialingTranslator, setIsDialingTranslator] = useAtom(
    isDialingTranslatorAtom,
  );
  const [, setVideoCallRoomToken] = useAtom(videoCallRoomTokenAtom);
  const [callPanelOpen, setCallPanelOpen] = useAtom(callPanelOpenAtom);
  const { data: patient } = useGetPatient(patientId);
  const { data: languageLineAddress } = useConfigCatFeatureFlag(
    "languageline-address",
  );

  // Hook for creating the meeting room
  const { mutateAsync: createMeeting, isPending: isCreatingMeeting } =
    useCreateVideoVisitMeeting();

  const { mutateAsync: completeMeeting } = useCompleteVideoVisitMeeting({
    shouldShowToast: false,
  });

  // With no option, dials the default `languageline-address` interpreter (used
  // by the automatic preferred-language dial-out). Providers pass an option
  // when manually picking an interpreter from the Tray.
  const addTranslator = useCallback(
    (option?: TranslatorOption) => {
      // startDialOut rejects until the call has joined
      if (
        !callObject ||
        translatorId ||
        isDialingTranslator ||
        callObject.meetingState() !== "joined-meeting"
      ) {
        return;
      }
      const video = option?.video ?? true;
      // Set synchronously, before the dial-out promise resolves, so the Tray
      // can disable the Add control immediately. Otherwise translatorId stays
      // null for the seconds it takes the SIP leg to ring, and a provider can
      // dial a second interpreter on top of the first.
      setIsDialingTranslator(true);
      callObject
        .startDialOut({
          sipUri: option?.sipUri ?? (languageLineAddress as string),
          video,
          userId: TRANSLATOR_USER_ID,
          displayName: video
            ? TRANSLATOR_DISPLAY_NAME
            : TRANSLATOR_AUDIO_ONLY_DISPLAY_NAME,
        })
        .then((e) => {
          setTranslatorId(e?.session?.sessionId || null);
          setIsDialingTranslator(false);
        })
        .catch((e) => {
          captureError(e.error?.msg);
          setIsDialingTranslator(false);
          toast.error("Couldn't connect the interpreter");
        });
    },
    [
      callObject,
      setTranslatorId,
      translatorId,
      isDialingTranslator,
      setIsDialingTranslator,
      languageLineAddress,
    ],
  );

  const removeTranslator = useCallback(() => {
    if (!callObject || !translatorId) {
      return;
    }
    callObject
      .stopDialOut({ sessionId: translatorId })
      .then((_) => {
        setTranslatorId(null);
        setIsDialingTranslator(false);
      })
      .catch((e) => {
        if (!(translatorId in callObject.participants)) {
          setTranslatorId(null);
        }
        setIsDialingTranslator(false);
        captureError(e.error?.msg);
      });
  }, [callObject, translatorId, setTranslatorId, setIsDialingTranslator]);

  // Function to join the meeting after getting token/url
  const joinMeeting = useCallback(
    async (token: string, url: string, roomName: string) => {
      if (!callObject) return;
      setVideoCallRoomToken(token);
      await callObject
        .join({ token, url: `${url}/${roomName}` })
        .then(() => {
          if (patient?.preferredLanguage === "es") {
            addTranslator();
          }
        })
        .catch((e) => {
          captureError(
            e instanceof Error
              ? e
              : new Error(String(e) || "Failed to join call"),
          );
          setVideoCallRoomToken(null);
          callObject?.destroy();
          toast.error("Failed to join video call");
        });
    },
    [
      callObject,
      setVideoCallRoomToken,
      addTranslator,
      patient?.preferredLanguage,
    ],
  );

  const leaveCall = useCallback(() => {
    if (translatorId) {
      removeTranslator();
    }
    callObject
      ?.leave()
      .catch((e) => {
        captureError(e?.error?.msg);
      })
      .finally(async () => {
        try {
          await completeMeeting({
            encounterId: encounterId as string,
            completed: true,
          });
        } catch (error) {
          captureError(
            error instanceof Error
              ? error
              : new Error("Failed to complete video visit"),
          );
        }
        callObject?.destroy();
        setCallPanelOpen(false);
        setVideoCallRoomToken(null);
        setTranslatorId(null);
        setIsDialingTranslator(false);
      });
  }, [
    callObject,
    setCallPanelOpen,
    setTranslatorId,
    setIsDialingTranslator,
    setVideoCallRoomToken,
    translatorId,
    removeTranslator,
    completeMeeting,
    encounterId,
  ]);

  // Function to handle the entire toggle process
  const toggleVideoVisitCall = useCallback(
    async (encounterId: string | undefined) => {
      if (callPanelOpen) {
        leaveCall();
      } else {
        setCallPanelOpen(true);
        if (!encounterId) {
          setCallPanelOpen(false);
          captureError(new Error("Encounter ID not found for video call"));
          return;
        }
        try {
          const meetingData = await createMeeting({
            encounterId: encounterId.toString(),
          });
          const { token, url, roomName } = meetingData.data;

          if (!token || !url || !roomName) {
            throw new Error("Invalid meeting data received");
          }
          // Calls joinMeeting, error handling is inside joinMeeting's catch blocks
          await joinMeeting(token, url, roomName);
        } catch (error) {
          // This catch handles errors from createMeeting or the re-thrown error from joinMeeting
          console.warn("Failed to create or join meeting:", error);
          captureError(
            error instanceof Error
              ? error
              : new Error("Failed to create or join meeting"),
          );
          setCallPanelOpen(false);
          callObject?.destroy();
          setVideoCallRoomToken(null);
          setTranslatorId(null);
          setIsDialingTranslator(false);
        }
      }
    },
    [
      callPanelOpen,
      leaveCall,
      setCallPanelOpen,
      createMeeting,
      joinMeeting,
      callObject,
      setVideoCallRoomToken,
      setTranslatorId,
      setIsDialingTranslator,
    ],
  );

  return {
    callObject,
    toggleVideoVisitCall,
    isCallPending: isCreatingMeeting,
    leaveCall,
    addTranslator,
    removeTranslator,
  };
};
